import React from "react";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import { aboutUsService } from "../services/dataService";
import bailImage from "../../assets/mig5.jpg";

const AboutUsSection = () => {
  // Pull existing data but override copy to match the USCIS Q&A flow
  const data = aboutUsService.getAboutUsData();

  // Merge with platform-aligned English content while preserving structure/logic
  const content = {
    ...data,
    // Header & body copy
    title: "About Our USCIS Form Platform",
    descriptionParagraph1:
      "We replace complex USCIS forms with a plain-English interview. Choose a form, answer simple questions, and we map your answers to the correct USCIS fields with built-in validations and smart checks.",
    descriptionParagraph2:
      "When you’re done, pay securely and download a ready-to-file PDF. Need legal help? You can hand off your case to a licensed attorney or a law firm right from your dashboard.",
    // Contact block
    contactInfo: {
      ...(data.contactInfo || {}),
      callsLabel: "Talk to a specialist",
      phoneNumber: (data.contactInfo && data.contactInfo.phoneNumber) || "+1 (000) 000-0000",
    },
    // Bottom feature cards (keep same layout/animation; just refresh the copy)
    featureCards: [
      {
        id: 1,
        icon: "dashboard",
        title: "Smart Q&A",
        description:
          "Answer guided questions instead of filling complicated fields—faster and less stressful.",
      },
      {
        id: 2,
        icon: "request",
        title: "Auto-Checks",
        description:
          "We flag missing or inconsistent details before you finalize, reducing avoidable rework.",
      },
      {
        id: 3,
        icon: "ongoing",
        title: "Document Checklist",
        description:
          "Know exactly what to prepare. Upload and manage supporting documents in one place.",
      },
      {
        id: 4,
        icon: "complete",
        title: "Attorney Handoff",
        description:
          "Request a licensed attorney or a law firm to review and continue your case when needed.",
      },
    ],
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case "dashboard":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
            />
          </svg>
        );
      case "request":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
        );
      case "ongoing":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case "complete":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section className="bg-gray-50 py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Left Side - Image */}
          <div className="lg:col-span-2">
            <ScrollAnimation direction="left" delay={0.1}>
              <motion.div
                className="relative group"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={bailImage}
                    alt="USCIS form assistance platform"
                    className="w-full h-[380px] object-cover"
                  />
                  <div className="absolute inset-0 bg-blue-900/10"></div>
                </div>

                {/* Floating Badge */}
                <motion.div
                  className="d-none absolute -bottom-4 -right-4 bg-white rounded-lg shadow-xl p-4 border-2 border-blue-600"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, type: "spring" }}
                >
                  <div className="text-center">
                    <div className="text-sm font-bold text-blue-600">Ready-to-File</div>
                    <div className="text-xs text-gray-600 font-medium">Filled PDFs</div>
                  </div>
                </motion.div>
              </motion.div>
            </ScrollAnimation>
          </div>

          {/* Right Side - Content */}
          <div className="lg:col-span-3">
            <ScrollAnimation direction="right" delay={0.2}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">
                  About Us
                </span>

                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  {content.title}
                </h2>

                <p className="text-gray-600 mb-4 leading-relaxed">
                  {content.descriptionParagraph1}
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {content.descriptionParagraph2}
                </p>

                {/* Stats Mini Cards (neutral, no guarantees) */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-600 rounded-lg p-4 text-white">
                    <div className="text-2xl font-bold">Less than 3 minutes</div>
                    <div className="text-sm text-blue-100">Avg. Response time</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-600">
                    <div className="text-2xl font-bold text-blue-600">24/7</div>
                    <div className="text-sm text-gray-600">Support</div>
                  </div>
                </div>

                {/* Contact */}
                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="bg-blue-600 rounded-lg p-2">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{content.contactInfo.callsLabel}</p>
                    <p className="text-xl font-bold text-gray-900">{content.contactInfo.phoneNumber}</p>
                  </div>
                </div>
              </motion.div>
            </ScrollAnimation>
          </div>
        </div>

        {/* Feature Cards - Horizontal */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {content.featureCards.map((card, index) => (
            <ScrollAnimation key={card.id} direction="up" delay={index * 0.05}>
              <motion.div
                className="bg-white rounded-lg p-5 text-center border border-gray-200 hover:border-blue-600 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index, duration: 0.4 }}
              >
                <div className="bg-blue-50 rounded-lg w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <div className="text-blue-600 scale-75">{getIcon(card.icon)}</div>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{card.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{card.description}</p>
              </motion.div>
            </ScrollAnimation>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;
